import './AdvanceTable.scss';
import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import { Table, TableProps, Spinner } from 'react-bootstrap';
import { flexRender } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from 'providers/AppProvider';

interface RowData {
  name?: string;
  node_instance_id?: string;
  type?: any;
  parent_node_instance_id?: number | string;
  // add other fields as needed
}

interface AdvanceTableProps {
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string | ((row: any) => string | undefined);
  tableProps?: TableProps;
  hasFooter?: boolean;
  expandableRowRenderer?: (row: any) => any;
  expandedRowIds?: any;
  topScrollBar?: boolean;
  onRowClick?: (row: RowData) => void;
  isLoading?: boolean;
  /**
   * The row id (as produced by the table's `getRowId`) of the row a side pane is
   * showing. It is highlighted, and kept in view when it changes or when the set of
   * visible columns does — collapsing the columns changes row heights, which would
   * otherwise move the selected row out from under the user.
   */
  selectedRowId?: string;
}

// A click on a control inside a row belongs to that control, not to the row: ticking
// the selection checkbox or opening the actions dropdown must not also select the row.
// `.form-check` is the checkbox's wrapper, whose padding surrounds the box itself, so a
// near miss still counts as aiming for the checkbox.
// The second test covers React portals (a dropdown menu, a dialog opened from the
// row), whose clicks bubble through the React tree but not through the DOM.
const isRowClick = (event: React.MouseEvent<HTMLTableRowElement>): boolean => {
  const target = event.target;
  if (!(target instanceof Element) || !event.currentTarget.contains(target)) return false;
  return target.closest('input, button, a, label, select, textarea, .form-check, .dropdown-menu') === null;
};

const AdvanceTable = ({
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
  hasFooter,
  expandableRowRenderer,
  expandedRowIds,
  topScrollBar,
  onRowClick,
  isLoading,
  selectedRowId,
}: AdvanceTableProps) => {
  const table = useAdvanceTableContext();
  const { getRowModel, getFlatHeaders, getFooterGroups } = table;
  const rowCount = getRowModel().rows.length;

  const {
    config: { theme },
  } = useAppContext();

  const topScrollRef = useRef<HTMLDivElement>(null);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.scrollWidth);
    }
  }, [getRowModel().rows]);

  const visibleColumnCount = getFlatHeaders().length;
  useEffect(() => {
    if (selectedRowId === undefined || !mainScrollRef.current) return;
    const rows = mainScrollRef.current.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]');
    Array.from(rows)
      .find((row) => row.dataset.rowId === selectedRowId)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedRowId, visibleColumnCount]);

  // Sync scrolling between top and main scroll areas
  const handleTopScroll = () => {
    if (topScrollRef.current && mainScrollRef.current) {
      mainScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleMainScroll = () => {
    if (topScrollRef.current && mainScrollRef.current) {
      topScrollRef.current.scrollLeft = mainScrollRef.current.scrollLeft;
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top scrollbar */}
      {topScrollBar && (
        <div
          ref={topScrollRef}
          onScroll={handleTopScroll}
          style={
            {
              flex: '0 0 auto',
              overflowX: 'auto',
              overflowY: 'hidden',
              height: '9px',
              backgroundColor: '#F6F7FA',
              marginBottom: '10px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#b4bddbe6 #f1f1f1',
              pointerEvents: isLoading ? 'none' : 'auto',
            } as React.CSSProperties
          }
        >
          <div
            style={{
              height: '1px',
              width: `${tableWidth}px`,
              minWidth: '100%',
            }}
          />
        </div>
      )}

      <div
        ref={mainScrollRef}
        className="table-scrollbar-visible"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? 'none' : 'auto',
        }}
        onScroll={handleMainScroll}
      >
        <Table {...tableProps} ref={tableRef}>
          <thead className={headerClassName}>
            <tr>
              {getFlatHeaders().map((header) => {
                const { column, isPlaceholder, getContext, id } = header;
                const { columnDef, getToggleSortingHandler } = column;
                const { meta = {}, header: columnHeader } = columnDef;
                const { customOnClick, headerProps, sortedDirection } = meta as {
                  customOnClick?: () => void;
                  headerProps?: {
                    className?: string;
                    style?: React.CSSProperties;
                  };
                  sortedDirection?: 'asc' | 'desc' | null;
                };

                const onClick = getToggleSortingHandler();
                const onClickHandler = customOnClick || onClick;
                // Use meta.sortedDirection if available (from useTableController), otherwise fall back to TanStack's state
                const sortDirection = sortedDirection || column.getIsSorted();
                const sortableClassNames = classNames(headerProps?.className, {
                  sortable: column.getCanSort(),
                });

                return (
                  <th
                    key={id}
                    {...headerProps}
                    className={sortableClassNames}
                    onClick={isLoading ? undefined : onClickHandler}
                    style={{
                      ...(headerProps?.style || {}),
                      position: 'sticky',
                      top: 0,
                      backgroundColor: theme === 'dark' ? '#141824' : '#f9fafb',
                      zIndex: 10,
                    }}
                  >
                    {isPlaceholder ? null : (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {flexRender(columnHeader, getContext())}
                        {sortDirection && (
                          <FontAwesomeIcon icon={sortDirection === 'asc' ? faArrowUp : faArrowDown} style={{ fontSize: '0.75em', opacity: 0.8 }} />
                        )}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={bodyClassName}>
            {getRowModel().rows.map((row) => {
              const rowData = row.original as RowData;

              return (
                <React.Fragment key={row.id}>
                  <tr
                    data-row-id={row.id}
                    className={classNames(typeof rowClassName === 'function' ? rowClassName(row) : rowClassName, {
                      'expandable-row': row.getCanExpand(),
                      expanded: row.getIsExpanded(),
                      'cursor-pointer': !!onRowClick && !isLoading,
                      'table-row-selected': selectedRowId !== undefined && row.id === selectedRowId,
                    })}
                    onClick={onRowClick && !isLoading ? (event) => isRowClick(event) && onRowClick(rowData) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} {...cell.column.columnDef.meta?.cellProps}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          {hasFooter && (
            <tfoot>
              {getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id} className="border-0">
                  {footerGroup.headers.map((header) => (
                    <th key={header.id} {...header.column.columnDef.meta?.footerProps}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </Table>
      </div>

      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
            zIndex: 1,
          }}
        >
          <Spinner animation="border" />
        </div>
      )}
    </div>
  );
};

export default AdvanceTable;
